import { useEffect, useState } from 'react'

// Freestar inserts and removes this element outside React's component tree.
export default function useStickyFooterOffset() {
    const [offset, setOffset] = useState(0)

    useEffect(() => {
        let footer: HTMLElement | null = null
        let frame = 0
        const measure = () => {
            const nextFooter = document.getElementById('fs-sticky-footer')
            if (nextFooter !== footer) {
                if (footer) resizeObserver.unobserve(footer)
                footer = nextFooter
                if (footer) resizeObserver.observe(footer)
            }
            const rect = footer?.getBoundingClientRect()
            const visible =
                footer &&
                rect &&
                rect.height > 0 &&
                rect.bottom > 0 &&
                getComputedStyle(footer).visibility !== 'hidden' &&
                getComputedStyle(footer).display !== 'none'
            setOffset(visible ? Math.max(0, window.innerHeight - rect.top) : 0)
        }
        const scheduleMeasure = () => {
            cancelAnimationFrame(frame)
            frame = requestAnimationFrame(measure)
        }
        const resizeObserver = new ResizeObserver(scheduleMeasure)
        const containsFooter = (node: Node) =>
            node instanceof Element &&
            (node.id === 'fs-sticky-footer' || node.contains(footer) || !!node.querySelector('#fs-sticky-footer'))
        const isFooterRelated = (target: Node) => !!footer && (footer.contains(target) || target.contains(footer))
        const mutationObserver = new MutationObserver((mutations) => {
            if (
                mutations.some(({ type, target, addedNodes, removedNodes }) => {
                    if (type === 'attributes') return isFooterRelated(target)
                    return (
                        footer?.contains(target) ||
                        Array.from(addedNodes).some(containsFooter) ||
                        Array.from(removedNodes).some(containsFooter)
                    )
                })
            ) {
                scheduleMeasure()
            }
        })
        mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class', 'hidden'],
        })
        const onTransitionEnd = (event: TransitionEvent) => {
            if (event.target instanceof Node && isFooterRelated(event.target)) scheduleMeasure()
        }
        window.addEventListener('resize', scheduleMeasure)
        document.addEventListener('transitionend', onTransitionEnd)
        measure()

        return () => {
            cancelAnimationFrame(frame)
            resizeObserver.disconnect()
            mutationObserver.disconnect()
            window.removeEventListener('resize', scheduleMeasure)
            document.removeEventListener('transitionend', onTransitionEnd)
        }
    }, [])

    return offset
}
